<script setup>
import { computed, ref } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue';
import { buildLinkAssetUrl } from '@/utils/assetUrls.js';

const props = defineProps({
    link: { type: Object, required: true },
    appearance: { type: Object, default: () => ({}) },
    buttonStyle: { type: [Object, Array, String], default: () => ({}) },
    DoBucket: String,
    DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

// Local state to control inline preview visibility (for classic + preview-only)
const showPreview = ref(false)

// Normalize display mode like TypeformCard does
function normalizeMode(raw){
  const s = String(raw || '').toLowerCase().trim()
  if (!s) return ''
  if (s === 'go-directly') return 'go-directly'
  if (s === 'preview-only') return 'preview-only'
  return s
}

const displayMode = computed(() => {
  const opt = props.link?.options || {}
  const music = opt.music || {}
  let m = normalizeMode(music.display_mode || props.link?.music_option)
  return m || 'preview-only'
})

function onClick(e) {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    const isPreviewOnly = displayMode.value === 'preview-only'
    const isClassic = (props.link?.options?.layout || 'classic') === 'classic'

    if (isClassic && isPreviewOnly) {
        // Route through parent handler so it can check locks and open unlock modal if needed
        emit('click', { link: props.link, mode: displayMode.value })
        return
    }
    // For other modes, also route through parent lock flow
    emit('click', { link: props.link, mode: displayMode.value })
}
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }

function isDeezer(){
    return (props.link?.platform || '').toLowerCase() === 'deezer'
}

const layout = computed(() => props.link?.options?.layout || 'classic')
const coverImageUrl = computed(() =>
    buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, props.link?.file),
)

// Whether the link should go directly to URL (no preview iframe)
const isGoDirectly = computed(() => {
    const opt = props.link?.options || {}
    const music = opt.music || {}
    const mode = music.display_mode || props.link?.music_option
    return String(mode).toLowerCase() === 'go-directly'
})

// Build Deezer widget URL when applicable, otherwise use provided preview
const embedSrc = computed(() => {
    const preview = props.link?.streamingService?.preview
    if (!isDeezer()) return preview || ''

    // Prefer an explicit content URL if provided, else fallback to preview
    const contentUrl = props.link?.streamingService?.url || props.link?.url || ''
    if (!contentUrl) return preview || ''

    // Try to extract type and numeric id from Deezer URL
    // Supported types: track, album, playlist, artist (fallback to track if unknown)
    try {
        const m = contentUrl.match(/deezer\.com\/(?:(?:[a-z]{2}\/)?)((track|album|playlist|artist))\/(\d+)/i)
        if (m) {
            const type = (m[2] || 'track').toLowerCase()
            const id = m[3]
            // You can choose dark/light; keeping dark to match typical theme
            return `https://widget.deezer.com/widget/dark/${type}/${id}`
        }
    } catch (e) {
        // ignore and fallback
    }

    // If we can't parse, return preview if any, else the contentUrl directly (backend may already provide an embeddable URL)
    return preview || contentUrl
})

// Compute the primary display text for the track meta
const displayPrimary = computed(() => {
    const dz = props.link?.streamingService || {}
    const album = (dz.album || '').trim()
    if (album && album.toLowerCase() !== 'unknown track') return album
    const title = (dz.title || '').trim()
    return title || props.link?.title || 'Unknown Track'
})

// Expose a method so parent can trigger inline preview after unlocking
// Mirrors SoundCloudCard's `playInline()`
defineExpose({
  playInline() {
    showPreview.value = true
  }
})
</script>

<template>
    <!-- FEATURED layout -->
    <GeneralEmbedButton
        v-if="layout==='featured'"
        class="deezer-card"
        :link="link"
        :appearance="appearance"
        :buttonStyle="buttonStyle"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
        :layout="layout"
        @click="onClick"
    >
        <!-- Header: left logo, centered title, right share with spacing -->
        <div class="dz-header-row mb-2 d-flex align-items-center justify-content-between" style="height:3rem;">
            <div class="left-icon d-flex align-items-center">
                <svg style="width:1.5rem; height:1.5rem"  version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="100%" viewBox="0 0 225 225" enable-background="new 0 0 225 225" xml:space="preserve">
           <path :fill="appearance.button_color" opacity="1.000000" stroke="none" d="
           M226.000000,87.000000
               C226.000000,133.666672 226.000000,179.833328 226.000000,226.000000
               C151.000000,226.000000 76.000000,226.000000 1.000000,226.000000
               C1.000000,151.000000 1.000000,76.000000 1.000000,1.000000
               C76.000000,1.000000 151.000000,1.000000 226.000000,1.000000
               C226.000000,29.500000 226.000000,58.000000 226.000000,87.000000
           M162.905594,17.272673
               C160.598938,23.104118 157.855499,28.808380 156.109909,34.803200
               C154.404373,40.660480 153.717194,46.814293 152.672775,52.361015
               C150.721313,45.682354 148.925232,38.597519 146.476364,31.745998
               C145.773697,29.780003 143.330994,27.513851 141.410263,27.225994
               C140.106598,27.030621 137.482620,29.785503 136.900253,31.669548
               C134.602722,39.102467 132.479660,46.638229 131.054993,54.278145
               C129.655731,61.781826 129.182114,69.458122 128.218597,77.781670
               C126.364548,72.699188 124.986343,68.279984 123.149040,64.060707
               C121.222481,59.636448 119.117218,54.379898 113.570129,54.319054
               C107.959572,54.257511 105.888931,59.567654 103.939064,63.968426
               C102.049896,68.232193 100.635590,72.706360 99.012726,77.088127
               C98.255379,74.664360 98.349129,72.351128 97.968529,70.118774
               C95.987572,58.499737 94.205307,46.829273 91.615593,35.343166
               C90.913177,32.227768 87.929901,27.671965 85.491730,27.327372
               C81.021378,26.695564 80.138519,31.939392 79.003174,35.427433
               C77.280716,40.719219 76.074829,46.179153 74.419220,52.438213
               C73.184235,45.982197 72.373955,40.375866 70.988525,34.915462
               C69.768227,30.105881 68.361710,25.229548 66.147400,20.830723
               C64.902710,18.358088 61.815598,15.205474 59.630417,15.259630
               C57.384075,15.315303 54.854080,18.633694 53.139774,21.020931
               C51.636913,23.113733 50.894032,25.838184 50.167969,28.384155
               C48.856606,32.982483 47.808289,37.655823 46.614124,42.442135
               C45.188457,38.374233 44.128609,34.334358 42.376263,30.621305
               C40.426418,26.489786 37.735271,26.562248 35.628586,30.667223
               C34.427834,33.006943 33.551792,35.582378 32.951935,38.150345
               C28.591034,56.819260 28.053104,75.814156 29.507353,94.772797
               C30.314522,105.295624 32.845463,115.704536 34.961418,126.091057
               C35.324085,127.871246 37.507763,129.280457 38.850212,130.861053
               C40.282768,129.398666 41.805668,128.011566 43.112122,126.444084
               C43.831635,125.580803 44.187309,124.414268 45.180500,122.446251
               C45.567886,124.897964 45.659653,126.209053 45.990562,127.456726
               C48.494053,136.895813 50.774841,146.409729 53.778587,155.688751
               C54.488350,157.881302 57.672256,160.635742 59.729416,160.645172
               C61.777298,160.654556 64.647949,157.932037 65.732513,155.711288
               C68.237762,150.581558 69.880135,145.030426 72.280556,138.548264
               C72.568863,141.409973 72.610863,143.330902 72.974236,145.188980
               C75.296371,157.063156 77.497185,168.968170 80.236420,180.747101
               C80.727097,182.857040 83.538841,184.427231 85.278908,186.246628
               C87.052856,184.480682 89.494316,183.016891 90.459412,180.884827
               C92.366959,176.670731 93.457733,172.086899 95.237358,166.566788
               C95.938019,171.153885 96.130096,174.649216 97.040970,177.945938
               C99.554604,187.043549 102.105522,196.149414 105.195107,205.060120
               C106.412148,208.570206 108.748596,212.077103 113.354279,212.150833
               C118.233940,212.228943 120.266487,208.465378 121.938370,204.877747
               C123.964943,200.529022 125.809540,196.008957 126.994896,191.374298
               C128.939957,183.769226 130.290436,176.012070 132.007446,167.762680
               C133.762482,172.820267 135.043472,177.419861 136.996368,181.713867
               C137.833282,183.554062 140.090149,185.952087 141.661530,185.922913
               C143.298538,185.892532 145.740234,183.522430 146.343979,181.686600
               C148.566971,174.926956 150.533630,168.035675 151.910309,161.058502
               C153.260742,154.214417 153.775467,147.205414 154.656967,140.268799
               C155.957825,141.678131 156.392761,143.195404 156.923843,144.678253
               C158.154541,148.114517 158.970825,151.815277 160.835327,154.871902
               C162.336945,157.333618 165.165375,160.632736 167.337280,160.583252
               C169.582550,160.532059 172.631622,157.281982 173.760468,154.735809
               C176.097397,149.464706 177.620972,143.790100 179.049622,138.172211
               C180.286407,133.308807 180.929993,128.294525 182.017807,122.352699
               C183.084366,124.753059 183.454330,126.289505 184.346207,127.410828
               C185.408585,128.746536 186.821335,130.412338 188.269226,130.617783
               C189.227829,130.753815 191.027405,128.771454 191.556122,127.394127
               C192.978119,123.689812 194.269669,119.866341 194.994354,115.974968
               C198.834808,95.352837 198.949417,74.555954 196.731750,53.792145
               C195.927078,46.258080 193.668488,38.857063 191.731598,31.485117
               C191.348785,30.028128 189.188202,27.830837 188.223801,28.009241
               C186.670517,28.296585 184.876862,30.026638 184.171112,31.591932
               C182.622894,35.025707 181.692520,38.738060 180.372040,42.756054
               C179.875809,40.558987 179.630722,38.727966 179.044846,37.013401
               C176.904892,30.751041 175.032455,24.354242 172.303497,18.351683
               C170.551163,14.497257 167.133514,13.174469 162.905594,17.272673
           M25.560867,64.112633
               C24.946274,61.199818 24.620342,58.181374 23.607773,55.414188
               C23.009661,53.779644 21.324863,52.542740 20.128752,51.127018
               C19.079569,52.515236 17.484095,53.757385 17.067677,55.314793
               C13.752948,67.711876 13.755914,80.197739 16.994047,92.603165
               C17.427534,94.263885 19.198990,95.575363 20.350861,97.048569
               C21.559355,95.419388 23.656025,93.878136 23.827463,92.146294
               C24.720434,83.125603 25.123497,74.056427 25.560867,64.112633
           M212.120804,68.609619
               C211.470459,64.352478 211.052658,60.039932 210.058060,55.864799
               C209.646225,54.136009 207.999359,52.701427 206.914169,51.133041
               C205.647415,52.697392 203.701019,54.079056 203.223495,55.855549
               C199.963074,67.985146 200.046509,80.221436 203.175476,92.367393
               C203.618652,94.087715 205.309631,95.486588 206.423065,97.034241
               C207.678894,95.471771 209.813370,94.026039 210.039154,92.326706
               C211.041260,84.784409 211.496033,77.169380 212.120804,68.609619
           z"></path>
                    <path fill="#A239FF" opacity="1.000000" stroke="none" d="
           M163.155792,16.982449
               C167.133514,13.174469 170.551163,14.497257 172.303497,18.351683
               C175.032455,24.354242 176.904892,30.751041 179.044846,37.013401
               C179.630722,38.727966 179.875809,40.558987 180.372040,42.756054
               C181.692520,38.738060 182.622894,35.025707 184.171112,31.591932
               C184.876862,30.026638 186.670517,28.296585 188.223801,28.009241
               C189.188202,27.830837 191.348785,30.028128 191.731598,31.485117
               C193.668488,38.857063 195.927078,46.258080 196.731750,53.792145
               C198.949417,74.555954 198.834808,95.352837 194.994354,115.974968
               C194.269669,119.866341 192.978119,123.689812 191.556122,127.394127
               C191.027405,128.771454 189.227829,130.753815 188.269226,130.617783
               C186.821335,130.412338 185.408585,128.746536 184.346207,127.410828
               C183.454330,126.289505 183.084366,124.753059 182.017807,122.352699
               C180.929993,128.294525 180.286407,133.308807 179.049622,138.172211
               C177.620972,143.790100 176.097397,149.464706 173.760468,154.735809
               C172.631622,157.281982 169.582550,160.532059 167.337280,160.583252
               C165.165375,160.632736 162.336945,157.333618 160.835327,154.871902
               C158.970825,151.815277 158.154541,148.114517 156.923843,144.678253
               C156.392761,143.195404 155.957825,141.678131 154.656967,140.268799
               C153.775467,147.205414 153.260742,154.214417 151.910309,161.058502
               C150.533630,168.035675 148.566971,174.926956 146.343979,181.686600
               C145.740234,183.522430 143.298538,185.892532 141.661530,185.922913
               C140.090149,185.952087 137.833282,183.554062 136.996368,181.713867
               C135.043472,177.419861 133.762482,172.820267 132.007446,167.762680
               C130.290436,176.012070 128.939957,183.769226 126.994896,191.374298
               C125.809540,196.008957 123.964943,200.529022 121.938370,204.877747
               C120.266487,208.465378 118.233940,212.228943 113.354279,212.150833
               C108.748596,212.077103 106.412148,208.570206 105.195107,205.060120
               C102.105522,196.149414 99.554604,187.043549 97.040970,177.945938
               C96.130096,174.649216 95.938019,171.153885 95.237358,166.566788
               C93.457733,172.086899 92.366959,176.670731 90.459412,180.884827
               C89.494316,183.016891 87.052856,184.480682 85.278908,186.246628
               C83.538841,184.427231 80.727097,182.857040 80.236420,180.747101
               C77.497185,168.968170 75.296371,157.063156 72.974236,145.188980
               C72.610863,143.330902 72.568863,141.409973 72.280556,138.548264
               C69.880135,145.030426 68.237762,150.581558 65.732513,155.711288
               C64.647949,157.932037 61.777298,160.654556 59.729416,160.645172
               C57.672256,160.635742 54.488350,157.881302 53.778587,155.688751
               C50.774841,146.409729 48.494053,136.895813 45.990562,127.456726
               C45.659653,126.209053 45.567886,124.897964 45.180500,122.446251
               C44.187309,124.414268 43.831635,125.580803 43.112122,126.444084
               C41.805668,128.011566 40.282768,129.398666 38.850216,130.861053
               C37.507763,129.280457 35.324085,127.871246 34.961418,126.091057
               C32.845463,115.704536 30.314522,105.295624 29.507353,94.772797
               C28.053104,75.814156 28.591034,56.819260 32.951935,38.150345
               C33.551792,35.582378 34.427834,33.006943 35.628586,30.667223
               C37.735271,26.562248 40.426418,26.489786 42.376263,30.621305
               C44.128609,34.334358 45.188457,38.374233 46.614124,42.442135
               C47.808289,37.655823 48.856606,32.982483 50.167969,28.384155
               C50.894032,25.838184 51.636913,23.113733 53.139774,21.020931
               C54.854080,18.633694 57.384075,15.315303 59.630417,15.259630
               C61.815598,15.205474 64.902710,18.358088 66.147400,20.830723
               C68.361710,25.229548 69.768227,30.105881 70.988525,34.915462
               C72.373955,40.375866 73.184235,45.982197 74.419220,52.438213
               C76.074829,46.179153 77.280716,40.719219 79.003174,35.427433
               C80.138519,31.939392 81.021378,26.695564 85.491730,27.327372
               C87.929901,27.671965 90.913177,32.227768 91.615593,35.343166
               C94.205307,46.829273 95.987572,58.499737 97.968529,70.118774
               C98.349129,72.351128 98.255379,74.664360 99.012726,77.088127
               C100.635590,72.706360 102.049896,68.232193 103.939064,63.968426
               C105.888931,59.567654 107.959572,54.257511 113.570129,54.319054
               C119.117218,54.379898 121.222481,59.636448 123.149040,64.060707
               C124.986343,68.279984 126.364548,72.699188 128.218597,77.781670
               C129.182114,69.458122 129.655731,61.781826 131.054993,54.278145
               C132.479660,46.638229 134.602722,39.102467 136.900253,31.669548
               C137.482620,29.785503 140.106598,27.030621 141.410263,27.225994
               C143.330994,27.513851 145.773697,29.780003 146.476364,31.745998
               C148.925232,38.597519 150.721313,45.682354 152.672775,52.361015
               C153.717194,46.814293 154.404373,40.660480 156.109909,34.803200
               C157.855499,28.808380 160.598938,23.104118 163.155792,16.982449
           z"></path>
                    <path fill="#A33AFF" opacity="1.000000" stroke="none" d="
           M25.628971,64.558380
               C25.123497,74.056427 24.720434,83.125603 23.827463,92.146294
               C23.656025,93.878136 21.559355,95.419388 20.350861,97.048569
               C19.198990,95.575363 17.427534,94.263885 16.994047,92.603165
               C13.755914,80.197739 13.752948,67.711876 17.067677,55.314793
               C17.484095,53.757385 19.079569,52.515236 20.128754,51.127018
               C21.324863,52.542740 23.009661,53.779644 23.607773,55.414188
               C24.620342,58.181374 24.946274,61.199818 25.628971,64.558380
           z"></path>
                    <path fill="#A33AFF" opacity="1.000000" stroke="none" d="
           M212.134796,69.095139
               C211.496033,77.169380 211.041260,84.784409 210.039154,92.326706
               C209.813370,94.026039 207.678894,95.471771 206.423065,97.034241
               C205.309631,95.486588 203.618652,94.087715 203.175476,92.367393
               C200.046509,80.221436 199.963074,67.985146 203.223495,55.855549
               C203.701019,54.079056 205.647415,52.697392 206.914169,51.133041
               C207.999359,52.701427 209.646225,54.136009 210.058060,55.864799
               C211.052658,60.039932 211.470459,64.352478 212.134796,69.095139
           z"></path>
           </svg>
            </div>
            <CardTitle class="flex-grow-1" :title="link.title" layout="classic" align="center" :max="100" style="min-width:0"/>
            <span class="share-wrapper d-flex align-items-center" aria-label="Share">
            <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="SVGRepo_iconCarrier">
                <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                      d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
              </g>
            </svg>
      </span>
        </div>

        <!-- Media area: optional static image for go-directly, iframe for preview -->
        <div class="text-center mb-3">
            <!-- Show static cover image when 'go-directly' is selected -->
            <div v-if="isGoDirectly"
                 class="mx-auto"
           :style="{
              width: '100%',
              height: '200px',
              borderRadius: '12px 12px 0 0',
              backgroundImage: coverImageUrl ? `url(${coverImageUrl})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
           }"
            ></div>

            <!-- Streaming preview iframe (hidden when 'go-directly') -->
            <iframe
                v-if="embedSrc && !isGoDirectly"
                :src="embedSrc"
                width="100%"
                height="200"
                frameborder="0"
                allow="autoplay; encrypted-media"
                allowfullscreen
            />

            <!-- Track meta -->
            <div class="mt-2 d-none">
                <small class="text-muted">{{ link?.streamingService?.title }}</small>
            </div>
        </div>
    </GeneralEmbedButton>

    <!-- CLASSIC layout -->
    <GeneralButton
        v-else
        class="deezer-card"
        :link="link"
        :appearance="appearance"
        :buttonStyle="buttonStyle"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
        :layout="layout"
        :hideLeftImage="showPreview"
        @click="onClick"
    >
        <template #title>
            <CardTitle :title="link.title" :artist="link?.streamingService?.title" layout="classic" align="start" :max="100" />
        </template>
        <template #actions>
            <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_iconCarrier">
                    <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                          d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
                </g>
            </svg>
        </template>
    <!-- Inline slide-down preview INSIDE the button for classic + preview-only -->
        <transition name="slide-fade">
            <div v-show="showPreview" class="inline-preview" @click.stop>
                <iframe
                    v-if="embedSrc"
                    :src="embedSrc"
                    width="100%"
                    height="200"
                    frameborder="0"
                    allow="autoplay; encrypted-media"
                    allowfullscreen
                />
            </div>
        </transition>
    </GeneralButton>
  </template>

<style scoped>
.deezer-card :deep(iframe){ border: 0; }

.inline-preview {
  overflow: hidden;
  padding-top: 10px; /* increased spacing between title row and preview when sliding down */
}
/* Slide down animation */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-fade-enter-to,
.slide-fade-leave-from {
  max-height: 260px; /* 200 iframe + paddings */
  opacity: 1;
}

/* Featured header row spacing and truncation */
.dz-header-row { padding-top:6px; padding-left:12px; padding-right:12px; gap:8px; }
.dz-header-row .left-icon { margin-right: 8px; }
.dz-header-row .share-wrapper { margin-left: 8px; }
.dz-header-row .title { flex: 1; text-align: center; padding: 0 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
